import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function Flyover({
    anchorRef,
    anchorRect,
    onClose,
    children,
    preferredPlacement = 'bottom',
    align = 'end',
    width,
    offset = 8,
    className = '',
    backdropClassName = '',
    zIndex = 1500,
    closeOnEsc = true,
}) {
    const menuRef = useRef(null);
    const [style, setStyle] = useState(null);

    const getRect = () =>
        anchorRect ||
        (anchorRef && anchorRef.current && anchorRef.current.getBoundingClientRect()) ||
        null;

    const compute = () => {
        const r = getRect();
        if (!r || !menuRef.current) return;

        const w = width || menuRef.current.offsetWidth;
        const h = menuRef.current.offsetHeight;

        const vpW = window.innerWidth;
        const vpH = window.innerHeight;

        let place = preferredPlacement;
        if (place === 'bottom' && r.bottom + offset + h > vpH - 8) place = 'top';
        if (place === 'top' && r.top - offset - h < 8) place = 'bottom';

        const top = place === 'bottom' ? r.bottom + offset : r.top - h - offset;

        let left;
        if (align === 'center') left = r.left + (r.width - w) / 2;
        else if (align === 'end') left = r.right - w;
        else left = r.left;

        const clampedLeft = Math.max(8, Math.min(vpW - w - 8, left));

        setStyle({
            position: 'absolute',
            top: Math.round(top + window.scrollY),
            left: Math.round(clampedLeft + window.scrollX),
            width: width || undefined,
            zIndex,
        });
    };

    useLayoutEffect(() => { compute(); }, [anchorRect, preferredPlacement, align, width, offset, children]);

    useEffect(() => {
        const on = () => compute();
        window.addEventListener('resize', on);
        window.addEventListener('scroll', on, true);
        return () => {
            window.removeEventListener('resize', on);
            window.removeEventListener('scroll', on, true);
        };
    }, []);

    useEffect(() => {
        if (!closeOnEsc) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [closeOnEsc, onClose]);

    const stopAll = (e) => { e.preventDefault(); e.stopPropagation(); };

    if (!getRect()) return null;

    return createPortal(
        <>
            <div
                className={`flyover__backdrop ${backdropClassName}`}
                style={{ position: 'fixed', inset: 0, zIndex: zIndex - 1, background: 'transparent' }}
                onPointerDown={() => onClose?.()}
            />
            <div
                ref={menuRef}
                className={`flyover ${className}`}
                style={style}
                onPointerDown={stopAll}
                onClick={stopAll}
            >
                {children}
            </div>
        </>,
        document.body
    );
}
